import React, { useEffect } from "react";
import { FlatList } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import {
  getUserRankingRequest,
  changeUserRankingPage,
  loadUserRankingRequest,
} from "@microdoing/shared/services/ranking/actions";
import { requestExternalUser } from "@microdoing/shared/services/externalProfile/actions";
import { Box, MySafeAreaView, Spinner } from "../../components/common";
import { getUserId } from "@microdoing/shared/services/user/selectors";
import Header from "./Header.js";
import i18n from "@microdoing/shared/i18n";
import UserRankingRow from "app/scenes/CurrentTrackScreen/ProgressScreen/UserRankingRow";
import theme from "app/Theme/Theme";
import WebDesktopView from "app/components/common/Web/WebDesktopView";
import { breadCrumbsUserRankingScreen } from "app/navigation/breadcrumb/BreadcrumbPaths";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import { useNavigation } from "@react-navigation/native";
import {
  getRankingHasResult,
  getRankingLoading,
  getRankingRequesting,
  getRankingUserRankingPage,
  getUserRankings,
} from "@microdoing/shared/services/ranking/selectors";
import { getCampaignId } from "@microdoing/shared/services/userCampaign/selectors";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import HeaderFullScreen from "app/components/common/HeaderFullScreen";
import ArrowLeft from "app/components/Icons/Arrow/ArrowLeft";
import normalize from "app/utils/normalize";

interface IProps {}

const UserRankingScreen = ({}: IProps) => {
  const userId = useSelector(getUserId);
  const requesting = useSelector(getRankingRequesting);
  const loading = useSelector(getRankingLoading);
  const userRankingPage = useSelector(getRankingUserRankingPage);
  const ranking = useSelector(getUserRankings);
  const hasResult = useSelector(getRankingHasResult);
  const campaignId = useSelector(getCampaignId);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const insets = useSafeAreaInsets();

  useEffect(() => {
    dispatch(changeUserRankingPage(0));
    dispatch(
      getUserRankingRequest({
        campaignId: campaignId,
        userId: userId,
        page: 0,
        limit: 10,
      }),
    );
  }, []);

  const keyExtractor = (item, index) => {
    return index.toString();
  };

  const navigateExternalProfile = id => () => {
    if (id === userId) {
      navigation.navigate("UserProfile");
    } else if (id) {
      dispatch(requestExternalUser({ userId: id }));
    }
  };

  const loadMore = () => {
    if (!requesting && !loading && hasResult) {
      dispatch(
        loadUserRankingRequest({
          campaignId: campaignId,
          page: userRankingPage,
          limit: 10,
        }),
      );
    }
  };

  const renderRanking = data => {
    const { index } = data;
    const { _id } = data.item;
    const isMe = _id === userId;

    return (
      <>
        <UserRankingRow
          user={data.item}
          rank={data.index + 1}
          key={_id}
          isUser={isMe}
          onPress={navigateExternalProfile(_id)}
        />
        {index !== ranking.length - 1 && (
          <Box borderTopWidth={1} borderTopColor="neutral.75" />
        )}
      </>
    );
  };

  return (
    <WebDesktopView breadcrumbsPath={breadCrumbsUserRankingScreen}>
      <MySafeAreaView
        flex={1}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        backgroundColor="neutral.white"
        testID={"user-ranking-screen"}
      >
        {!isWebDesktop && (
          <HeaderFullScreen
            title={i18n.t("player-ranking-title")}
            leftIcon={
              <ArrowLeft
                color={theme.colors["neutral.900"]}
                height={normalize(24)}
                width={normalize(24)}
              />
            }
          />
        )}
        <Box flex={1} width="100%" flexDirection="row">
          <FlatList
            data={ranking}
            renderItem={renderRanking}
            keyExtractor={keyExtractor}
            keyboardShouldPersistTaps="always"
            ListHeaderComponent={
              requesting ? (
                <>
                  <Spinner size={50} />
                </>
              ) : (
                <></>
              )
            }
            ListFooterComponent={
              loading ? (
                <>
                  <Spinner size={50} />
                </>
              ) : (
                <></>
              )
            }
            onEndReached={loadMore}
            onEndThreshold={0}
          />
        </Box>
      </MySafeAreaView>
    </WebDesktopView>
  );
};

export default UserRankingScreen;
